"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { CounterButton } from "./Button";

/**
 * The numeric keypad (DESIGN.md §6, §7): 56 px keys, 16 px legends, three
 * columns. Every screen that takes a figure by thumb uses this one — tender,
 * PIN, quantity, discount, cash count, odometer. Before it, four screens each
 * drew their own grid and each picked a different key height.
 *
 * It is deliberately uncontrolled about meaning: it hands up the key that was
 * pressed and the caller decides what the digits are for. A keypad that knows
 * about money cannot be used for a PIN.
 */
export type KeypadKey = "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "." | "backspace";

const DIGITS: readonly KeypadKey[] = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function Keypad({
  onKey,
  decimal = true,
  /** Replaces the bottom-left key — "00" on a cash screen, "Clear" on a PIN. */
  extra,
  ariaLabel = "Keypad",
  className,
}: Readonly<{
  onKey: (key: KeypadKey) => void;
  decimal?: boolean;
  extra?: { label: string; onPress: () => void };
  ariaLabel?: string;
  className?: string;
}>) {
  return (
    <div role="group" aria-label={ariaLabel} className={cn("grid grid-cols-3 gap-2", className)}>
      {DIGITS.map((digit) => (
        <KeypadButton key={digit} label={digit} onPress={() => onKey(digit)} />
      ))}
      {extra ? (
        <KeypadButton label={extra.label} onPress={extra.onPress} />
      ) : decimal ? (
        <KeypadButton label="." onPress={() => onKey(".")} />
      ) : (
        <span />
      )}
      <KeypadButton label="0" onPress={() => onKey("0")} />
      <KeypadButton label="⌫" srLabel="Backspace" onPress={() => onKey("backspace")} />
    </div>
  );
}

function KeypadButton({
  label,
  srLabel,
  onPress,
}: Readonly<{ label: string; srLabel?: string; onPress: () => void }>) {
  return (
    <CounterButton
      tone="secondary"
      size="large"
      onClick={onPress}
      aria-label={srLabel ?? label}
      // font-ledger: a keypad legend is a figure, and a sans never sets one.
      className="font-ledger text-base tabular-nums"
    >
      <span aria-hidden="true">{label}</span>
    </CounterButton>
  );
}
